import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { ChevronLeft } from "lucide-react";
import type { SubmitHandler } from "react-hook-form";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";

import { usePasswordRecovery } from "@/hooks/usePasswordRecovery";
import { ROUTES } from "@/router";
import type { ForgotPasswordFormValuesType } from "@/types";
import { forgotPasswordFormSchema } from "@/types";
import { Button, FormField, Input } from "@/ui";
import { formatSecondsToCounter } from "@/utils";

const RESEND_DELAY = 120;
const STORAGE_KEY_START_TIME = "forgot-password-start-time";
const STORAGE_KEY_EMAIL = "forgot-password-email";

export const ForgotPassword = () => {
  const [isLinkSent, setIsLinkSent] = useState(false);
  const [timer, setTimer] = useState(0);
  const [isResendEnabled, setIsResendEnabled] = useState(false);

  const { useSendForgotPasswordEmail } = usePasswordRecovery();
  const { mutate: sendEmail } = useSendForgotPasswordEmail();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormValuesType>({
    resolver: zodResolver(forgotPasswordFormSchema),
    defaultValues: {
      email: localStorage.getItem(STORAGE_KEY_EMAIL) ?? undefined,
    },
  });

  const onSubmit: SubmitHandler<ForgotPasswordFormValuesType> = (values) => {
    setIsLinkSent(true);
    setIsResendEnabled(false);
    setTimer(RESEND_DELAY);

    localStorage.setItem(STORAGE_KEY_START_TIME, Date.now().toString());
    localStorage.setItem(STORAGE_KEY_EMAIL, values.email);

    sendEmail(values.email);
  };

  useEffect(() => {
    const storedStartTime = localStorage.getItem(STORAGE_KEY_START_TIME);

    if (storedStartTime) {
      const startTime = Number(storedStartTime);
      const now = Date.now();
      const timeElapsedInSeconds = Math.floor((now - startTime) / 1000);
      if (timeElapsedInSeconds < RESEND_DELAY) {
        setIsLinkSent(true);
        setTimer(RESEND_DELAY - timeElapsedInSeconds);
      } else {
        setIsLinkSent(true);
        setTimer(0);
        setIsResendEnabled(true);
      }
    }
  }, []);

  useEffect(() => {
    if (isLinkSent && timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    }

    if (timer === 0 && isLinkSent) {
      setIsResendEnabled(true);
    }
  }, [timer, isLinkSent]);

  return (
    <>
      <Link
        to={ROUTES.login}
        className="relative flex w-full items-center justify-center"
      >
        <ChevronLeft className="absolute left-0 text-neutral-500" size={16} />
        <p className="text-neutral-600">Go back to Log in</p>
      </Link>

      <>
        <div className="flex flex-col gap-y-2 text-center text-primary-950">
          <h1 className="text-2xl font-bold">
            {isLinkSent ? "We've sent a link" : "Forgot password ?"}
          </h1>
          <p>
            {isLinkSent
              ? "If the email you entered in the previous step is registered, you will receive a link to reset your password."
              : "Enter your email address below, and we'll send you instructions to reset your password."}
          </p>
        </div>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-10"
        >
          <FormField label="Email" error={errors.email?.message}>
            <Input
              placeholder="Enter your email"
              {...register("email")}
              error={errors.email?.message}
              disabled={!isResendEnabled && isLinkSent}
            />
          </FormField>
          <div className="flex flex-col justify-center gap-y-6 text-center">
            {isLinkSent ? (
              <>
                <p className="text-xs text-neutral-500">
                  Remember to check your spam folder. Didn&rsquo;t get the email
                  yet?
                </p>
                <Button
                  className="w-full font-semibold"
                  disabled={!isResendEnabled}
                  type="submit"
                >
                  {isResendEnabled
                    ? `Resend link`
                    : `Wait ${formatSecondsToCounter(timer)}`}
                </Button>
              </>
            ) : (
              <Button className="mt-8 w-full font-semibold" type="submit">
                Send reset link
              </Button>
            )}
          </div>
        </form>
      </>
    </>
  );
};
